import React, { useState, useEffect } from 'react';
import MakeRequest from '../Helpers/MakeRequest';
import { Container, Row, Col, Table, Button, Form } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';

const EditRetailers = () => {
    const { l } = useOutletContext();
    const [retailer, setRetailer] = useState([]);
    const [saveDisabled, setSaveDisabled] = useState(true);

    useEffect(() => {
        const fetchRetailer = async () => {
            try {
                const response = await MakeRequest(null, `content/retailers`, 'get');
                setRetailer(response.data);
            } catch (error) {
                console.error("Failed to fetch retailer:", error);
            }
        };
        fetchRetailer();
    }, []);

    const handleChange = (e, idx, field) => {

        if (field === 'order' && e.target.value && e.target.value < 1) return; // Prevent order less than 1

        setSaveDisabled(false);
        const { value } = e.target;
        setRetailer(prev =>
            prev.map((item, i) =>
                i === idx ? { ...item, [field]: value } : item
            )
        );
    };

    const handleSaveChanges = async () => {

        for (let i = 0; i < retailer.length; i++) {
            const { company, link, title, content, order } = retailer[i];
            if (
                !company.trim() ||
                !link.trim() ||
                !title.trim() ||
                !content.trim() ||
                order === '' ||
                order === null ||
                typeof order === 'undefined'
            ) {
                alert(l("Alla fält är obligatoriska för varje återförsäljare. Fyll i alla fält.", "All fields are required for each retailer. Please fill in all fields."));
                return;
            }
        }

        const orderValues = retailer.map(r => r.order);
        const hasOrderDuplicates = orderValues.some((order, idx) => orderValues.indexOf(order) !== idx);
        if (hasOrderDuplicates) {
            alert(l("Dubbletter av ordningsvärden är inte tillåtna. Se till att varje återförsäljare har ett unikt ordningsvärde.", "Duplicate order values are not allowed. Please ensure each retailer has a unique order."));
            return;
        }

        const companyNames = retailer.map(r => r.company.trim().toLowerCase());
        const hasDuplicates = companyNames.some((name, idx) => companyNames.indexOf(name) !== idx);
        if (hasDuplicates) {
            alert(l("Dubbletter av företag är inte tillåtna. Se till att varje återförsäljare har ett unikt företagsnamn.", "Duplicate companies are not allowed. Please ensure each retailer has a unique company name."));
            return;
        }

        try {
            const response = await MakeRequest(retailer, `content/retailers`, 'post');
            if (response.status === 200) {
                setSaveDisabled(true);
                alert(l("Återförsäljare sparades.", "Retailers saved successfully"));
            } else {
                console.error("Failed to save changes:", response.statusText);
            }
        } catch (error) {
            console.error("Error saving changes:", error);
        }
    };

    const handleAddItem = () => {
        setSaveDisabled(false);
        // Find the highest existing order value and set new one to +1
        const maxOrder = retailer.length > 0
            ? Math.max(...retailer.map(r => Number(r.order) || 0))
            : 0;
        setRetailer([
            ...retailer,
            { company: '', link: '', title: '', content: '', order: maxOrder + 1 }
        ]);
    };

    const handleDeleteItem = async (idx) => {

        if (!retailer[idx]._id) {
            setRetailer(retailer.filter((_, i) => i !== idx));
            return;
        }

        if (window.confirm(l("Är du säker på att du vill ta bort denna återförsäljare?", "Are you sure you want to delete this retailer?"))) {
            try {
                const res = await MakeRequest({ id: retailer[idx]._id }, `content/retailers/${retailer[idx]._id}`, 'delete');
                if (res.status === 200) {
                    setRetailer(retailer.filter((_, i) => i !== idx));
                    alert(l("Återförsäljare borttagen.", "Retailer deleted successfully"));
                } else {
                    alert(l("Misslyckades med att ta bort återförsäljare.", "Failed to delete retailer"));
                }
            } catch (error) {
                console.error("Failed to delete retailer:", error);
            }
        }
    };

    return (
        <>
            <Row>
                <Form.Label column style={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
                    {l("Lägg till återförsäljare", "Add retailer")}
                    <Button
                        size="sm"
                        style={{ marginLeft: 10 }}
                        onClick={handleAddItem}
                        title={l("Lägg till ny rad", "Add new row")}
                    >
                        <i className="fa-solid fa-plus"></i>
                    </Button>
                </Form.Label>

                <Table striped bordered hover className="stack-on-md">
                    <thead>
                        <tr>
                            <th>{l("Företag", "Company")}</th>
                            <th>{l("Länk", "Link")}</th>
                            <th>{l("Titel", "Title")}</th>
                            <th>{l("Ordning", "Order")}</th>
                            <th></th>
                        </tr>
                    </thead>

                    <tbody>
                        {retailer.map((customer, idx) => (
                            <React.Fragment key={idx}>
                                <tr>
                                    <td data-label={l("Företag", "Company")}>
                                        <Form.Group controlId={`formCompany${idx}`}>
                                            <Form.Control
                                                type="text"
                                                placeholder={l("Företag", "Company")}
                                                onChange={(e) => handleChange(e, idx, "company")}
                                                value={customer.company}
                                            />
                                        </Form.Group>
                                    </td>

                                    <td data-label={l("Länk", "Link")}>
                                        <Form.Group controlId={`formLink${idx}`}>
                                            <Form.Control
                                                type="text"
                                                placeholder={l("Länk", "Link")}
                                                onChange={(e) => handleChange(e, idx, "link")}
                                                value={customer.link}
                                            />
                                        </Form.Group>
                                    </td>

                                    <td data-label={l("Titel", "Title")}>
                                        <Form.Group controlId={`formTitle${idx}`}>
                                            <Form.Control
                                                type="text"
                                                placeholder={l("Titel", "Title")}
                                                onChange={(e) => handleChange(e, idx, "title")}
                                                value={customer.title}
                                            />
                                        </Form.Group>
                                    </td>

                                    <td className="d-block d-lg-none" data-label={l("Innehåll", "Content")}>
                                        <Form.Group controlId={`formContent${idx}`}>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                placeholder={l("Innehåll", "Content")}
                                                onChange={(e) => handleChange(e, idx, "content")}
                                                value={customer.content}
                                            />
                                        </Form.Group>
                                    </td>

                                    <td data-label={l("Ordning", "Order")}>
                                        <Form.Control
                                            type="number"
                                            placeholder={l("Ordning", "Order")}
                                            min={1}
                                            onChange={(e) => handleChange(e, idx, "order")}
                                            value={customer.order}
                                        />
                                    </td>

                                    <td>
                                        <Button onClick={() => handleDeleteItem(idx)}>
                                            <i className="fa-solid fa-trash"></i>
                                        </Button>
                                    </td>
                                </tr>

                                {/* Independent row for textarea */}
                                <tr className="d-none d-lg-table-row">
                                    <td colSpan={5} data-label={l("Innehåll", "Content")}>
                                        <h6><b>{l("Innehåll", "Content")}</b></h6>
                                        <Form.Group controlId={`formContent${idx}`}>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                placeholder={l("Innehåll", "Content")}
                                                onChange={(e) => handleChange(e, idx, "content")}
                                                value={customer.content}
                                            />
                                        </Form.Group>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            </Row>

            <Row className="d-flex justify-content-center">
                <Button style={{ width: "200px" }} onClick={handleSaveChanges} disabled={saveDisabled}>
                    {l("Spara ändringar", "Save Changes")}
                </Button>
            </Row>
        </>
    );
};

export default EditRetailers;
